import PropTypes from 'prop-types';
import { Autocomplete, Chip, MenuItem, TextField } from '@mui/material';
import {
  getEffectiveProfileFieldType,
  PROFILE_FIELD_TYPES,
  sanitizeSingleSelectValue,
  sanitizeMultiSelectValue,
  getProfileFieldOptionNames,
  dedupeProfileFieldOptions,
} from './constants';

const requiredLabel = (name, isRequired) =>
  isRequired ? (
    <span>
      {name}
      <span style={{ color: 'red' }}>*</span>
    </span>
  ) : (
    name
  );

const ProfileFieldInput = ({
  field,
  value,
  onChange,
  error,
  helperText,
  sx,
  size = 'medium',
}) => {
  const fieldType = getEffectiveProfileFieldType(field);
  const isRequired = field?.is_checked === 1 || field?.is_checked === true;
  const label = requiredLabel(field?.profile_field_name, isRequired);
  const commonSx = { width: '100%', marginBottom: '10px', ...sx };

  if (fieldType === PROFILE_FIELD_TYPES.TEXT_INPUT) {
    return (
      <TextField
        name={String(field?.profile_field_id)}
        value={value ?? ''}
        onChange={(e) => onChange(field, e.target.value)}
        variant='outlined'
        size={size}
        sx={commonSx}
        label={label}
        error={error}
        helperText={helperText}
      />
    );
  }

  if (fieldType === PROFILE_FIELD_TYPES.DATE) {
    return (
      <TextField
        name={String(field?.profile_field_id)}
        value={value ?? ''}
        onChange={(e) => onChange(field, e.target.value)}
        variant='outlined'
        size={size}
        sx={commonSx}
        label={label}
        placeholder='MM/DD/YYYY'
        inputProps={{// NOSONAR - MUI v6 Autocomplete still requires InputProps for dropdown anchor
           inputMode: 'numeric', maxLength: 10 
          }}
        error={error}
        helperText={helperText || 'Format: MM/DD/YYYY'}
      />
    );
  }

  if (fieldType === PROFILE_FIELD_TYPES.MULTI_SELECT) {
    const optionNames = getProfileFieldOptionNames(field);
    const selectedValues = sanitizeMultiSelectValue(field, value);

    return (
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={optionNames}
        value={selectedValues}
        onChange={(_event, newValue) => onChange(field, newValue)}
        getOptionLabel={(option) => option}
        isOptionEqualToValue={(option, val) => option === val}
        limitTags={3}
        sx={commonSx}
        renderTags={(// NOSONAR - MUI v6 Autocomplete still requires InputProps for dropdown anchor
          tagValue, getTagProps
        ) =>
          tagValue.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                {...tagProps}
                label={option}
                size='small'
                sx={{ maxWidth: '100%' }}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={
              selectedValues.length === 0 ? 'Select one or more options' : ''
            }
            error={error}
            helperText={helperText}
            InputProps={{// NOSONAR - MUI v6 Autocomplete still requires InputProps for dropdown anchor
              ...params.InputProps,
              type: 'search',
            }}
          />
        )}
      />
    );
  }

  const selectValue = sanitizeSingleSelectValue(field, value);

  return (
    <TextField
      name={String(field?.profile_field_id)}
      value={selectValue}
      select
      onChange={(e) => onChange(field, e.target.value)}
      variant='outlined'
      size={size}
      sx={commonSx}
      label={label}
      error={error}
      helperText={helperText}
    >
      {dedupeProfileFieldOptions(field?.options).map((option, optionIndex) => {
        const optionName = option.option_name ?? option.name;
        return (
          <MenuItem
            key={`${option.option_id ?? optionName}-${optionIndex}`}
            value={optionName}
            sx={{ textTransform: 'capitalize' }}
          >
            {optionName}
          </MenuItem>
        );
      })}
    </TextField>
  );
};

ProfileFieldInput.propTypes = {
  field: PropTypes.object.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  sx: PropTypes.object,
  size: PropTypes.string,
};

export default ProfileFieldInput;
